import {sendRefTelemetry} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {
  CopilotSpaceGitHubIssueResource,
  CopilotSpaceGitHubPullRequestResource,
  CopilotSpaceResource,
  ResourceCounts,
} from '../../types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useDebounce} from '@github-ui/use-debounce'
import {PlusIcon, TrashIcon} from '@primer/octicons-react'
import {Banner, Button, Dialog, FormControl, IconButton, Stack, TextInput} from '@primer/react'
import {useEffect, useState} from 'react'

import type {UrlResourceValidationError} from './hooks/use-validate-url-resources'
import {UrlDetails, useValidateUrlResources} from './hooks/use-validate-url-resources'
import {UploadableFileExtensions} from './utils/uploadable-file-extensions'

interface GitHubUrlDialogProps {
  onClose: () => void
  onSave: (resources: Array<CopilotSpaceGitHubPullRequestResource | CopilotSpaceGitHubIssueResource>) => void
  resourcesToSave?: CopilotSpaceResource[]
  resourceCounts?: ResourceCounts
  owner: string | undefined
  spaceOwner?: string
}

export function GitHubUrlDialog({
  onClose,
  onSave,
  resourcesToSave,
  owner,
  spaceOwner = owner,
  resourceCounts,
}: GitHubUrlDialogProps) {
  const restrictedOwner = copilotFeatureFlags.spacesOrgScoping ? undefined : owner
  const [isUpdating, setIsUpdating] = useState(false)
  const [urls, setUrls] = useState<UrlDetails[]>(() => [new UrlDetails(crypto.randomUUID())])
  const [errorState, setErrorState] = useState(false)
  const [resourceCountError, setResourceCountError] = useState(false)

  const {validateUrlResources} = useValidateUrlResources()

  const handleSubmit = (e?: React.FormEvent<HTMLElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (isUpdating) return
    setIsUpdating(true)
    void updateUrlResources().finally(() => {
      setIsUpdating(false)
    })
  }

  // Helper to generate a unique key for deduplication
  function getResourceKey(resource: CopilotSpaceResource | UrlDetails): string {
    if (resource.type === 'github_file') {
      // Handle both possible property names for file resources
      const nwo = 'nwo' in resource ? resource.nwo : `${resource.owner}/${resource.repo}`
      const sha = resource.sha
      const filePath = 'filePath' in resource ? resource.filePath : resource.file_path
      return `${nwo}/github_file/${sha}/${filePath}`
    } else if (resource.type === 'github_issue' || resource.type === 'github_pull_request') {
      const nwo = 'nwo' in resource ? resource.nwo : `${resource.owner}/${resource.repo}`
      const number = resource.number
      const type = resource.type
      return `${nwo}/${number}/${type}`
    }
    return '' // Default return to satisfy the function's return type
  }

  const updateUrlResources = async () => {
    setErrorState(false)
    setResourceCountError(false)

    // De-duplicate and remove empty URLs
    //
    // A URL is treated as a duplicate if it has the same owner, repo, number, and type
    // We need to check against resourcesToSave and also check urls against itself
    const deduplicateUrlsAndRemoveEmpty = () => {
      const existingKeys = new Set(resourcesToSave?.map(r => getResourceKey(r)) ?? [])

      const seen = new Set<string>()
      return urls.filter(url => {
        const key = getResourceKey(url)
        if (seen.has(key) || existingKeys.has(key) || url.url.trim() === '') {
          return false
        }
        seen.add(key)
        return true
      })
    }

    const urlResources = deduplicateUrlsAndRemoveEmpty()

    const hasValidExtension = (filePath: string) => {
      // Remove query parameters (e.g., ?copilot=true) and line number fragments (e.g., #L26, #L1-L10) from the path
      const pathWithoutQueryAndLines = filePath
        .replace(/\?.*/, '') // Remove everything after ? (query parameters)
        .replace(/#L\d+(-L\d+)?$/, '') // Remove line number fragments

      // Matches a dot followed by one or more characters at the end of a string
      // If there's no match, it's considered valid (e.g Dockerfile, Makefile)
      const match = pathWithoutQueryAndLines.match(/\.([^.]+)$/)
      if (!match) return true
      const extension = `.${match[1]?.toLowerCase()}`
      return UploadableFileExtensions.text.has(extension)
    }

    // If any of the URLs have an unknown type, incorrect owner, or invalid file extension (for file URLs), set error state
    const invalidUrls = urlResources.filter(
      url =>
        url.type === 'unknown' ||
        (restrictedOwner && url.owner !== restrictedOwner) ||
        (url.file_path && !hasValidExtension(url.file_path)),
    )
    if (invalidUrls.length > 0) {
      const updatedUrls = urls.map(url => {
        if (restrictedOwner && url.owner && url.owner !== restrictedOwner) {
          return {...url, errorMessage: `This doesn't belong to the ${restrictedOwner} organization.`}
        } else if (url.file_path && !hasValidExtension(url.file_path)) {
          return {...url, errorMessage: 'Unsupported file type'}
        } else if (invalidUrls.some(invalid => invalid.id === url.id)) {
          return {...url, errorMessage: 'Invalid URL'}
        }
        return url
      })

      setUrls(updatedUrls)
      setErrorState(true)
      return
    }

    if (urlResources.length === 0) {
      onClose()
      return
    }

    try {
      const res = await validateUrlResources({input: urlResources, spaceOwner})
      onSave(res)
      sendRefTelemetry(res, 'url-dialog')
      onClose()
    } catch (errors: unknown) {
      // If errors are caught, one or more URLs are invalid
      if (Array.isArray(errors)) {
        for (const error of errors as UrlResourceValidationError[]) {
          const resource = urls.find(r => r.id === error.id)
          if (resource) {
            resource.errorMessage = 'Resource not found'
          }
        }
        setErrorState(true)
      }
    }
  }

  // Check if adding another URL would exceed the limit
  const wouldExceedOnAdd = resourceCounts
    ? resourceCounts.currentResourceCount + urls.length + 1 > resourceCounts.maxResourceCount
    : false

  const AddUrlTextInputRow = () => {
    if (wouldExceedOnAdd) {
      setResourceCountError(true)
      return
    }
    setUrls([...urls, new UrlDetails(crypto.randomUUID())])
  }

  const resourceCountErrorMessage =
    resourceCountError && resourceCounts
      ? `You can select a maximum of ${resourceCounts.maxResourceCount} sources.`
      : null

  const checkAndClearResourceCountError = (newUrls: UrlDetails[]) => {
    if (resourceCountError && resourceCounts) {
      const wouldStillExceed = resourceCounts.currentResourceCount + newUrls.length > resourceCounts.maxResourceCount
      if (!wouldStillExceed) {
        setResourceCountError(false)
      }
    }
  }

  const removeUrlTextInputRow = (index: number) => {
    const tmpUrls = [...urls]
    tmpUrls.splice(index, 1)
    setUrls(tmpUrls)
    checkAndClearResourceCountError(tmpUrls)
  }

  const onUrlChange = (index: number, value: string) => {
    // Will need to add a loading state so users do not submit the dialog before the urls are parsed
    const tmpUrls = [...urls]
    // use the existing empty UrlDetails object if it exists
    const parsedUrl = parseUrl(value, tmpUrls[index] || new UrlDetails(crypto.randomUUID()))
    tmpUrls[index] = parsedUrl
    setUrls(tmpUrls)
    checkAndClearResourceCountError(tmpUrls)
  }

  const parseUrl = (url: string, urlDetails: UrlDetails) => {
    // A very naive implementation for now until we implement validations
    // Copilot wrote this regex
    const issueRegex = /\/([^/]+)\/([^/]+)\/issues\/(\d+)/
    const prRegex = /\/([^/]+)\/([^/]+)\/pull\/(\d+)/
    const fileRegex = /\/([^/]+)\/([^/]+)\/blob\/([^/]+)\/(.+)$/

    const issueMatch = url.match(issueRegex)
    const prMatch = url.match(prRegex)
    const fileMatch = url.match(fileRegex)

    urlDetails.url = url
    if (issueMatch) {
      urlDetails.owner = issueMatch[1] ?? ''
      urlDetails.repo = issueMatch[2] ?? ''
      urlDetails.number = parseInt(issueMatch[3] ?? '0', 10)
      urlDetails.type = 'github_issue'
    } else if (prMatch) {
      urlDetails.owner = prMatch[1] ?? ''
      urlDetails.repo = prMatch[2] ?? ''
      urlDetails.number = parseInt(prMatch[3] ?? '0', 10)
      urlDetails.type = 'github_pull_request'
    } else if (fileMatch) {
      urlDetails.owner = fileMatch[1] ?? ''
      urlDetails.repo = fileMatch[2] ?? ''
      urlDetails.sha = fileMatch[3] ?? ''

      urlDetails.file_path = fileMatch[4] ?? ''
      urlDetails.type = 'github_file'
    }
    return urlDetails
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      width="large"
      title="Add via GitHub URL"
      onClose={onClose}
      renderFooter={() => (
        <Dialog.Footer>
          <div className="d-flex flex-items-center">
            {resourceCountErrorMessage && (
              <FormControl.Validation variant="error">{resourceCountErrorMessage}</FormControl.Validation>
            )}
          </div>
          <div className="d-flex flex-row flex-justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} loadingAnnouncement="Saving resource" loading={isUpdating}>
              Add
            </Button>
          </div>
        </Dialog.Footer>
      )}
    >
      {errorState && <Banner title="Failed to save URL(s)" variant="critical" className="mb-2" />}
      <p>
        Paste the URLs of the GitHub content{' '}
        {restrictedOwner && (
          <>
            from the <b>{restrictedOwner}</b> organization{' '}
          </>
        )}
        <span>you want to add. You can include issues and pull requests.</span>
      </p>
      <ScopedCommands commands={{'github:submit-form': () => handleSubmit()}}>
        <form onSubmit={handleSubmit}>
          <Stack direction="vertical" align="center">
            {urls.map((url, i) => (
              <div style={{width: '100%'}} key={url.id}>
                <UrlTextInputRow
                  index={i}
                  removeUrlTextInputRow={removeUrlTextInputRow}
                  inputCount={urls.length}
                  updateUrls={onUrlChange}
                  setIsUpdating={setIsUpdating}
                  urlValue={url.url}
                />
                {errorState && url.errorMessage !== undefined && (
                  <FormControl.Validation className="mt-1" variant="error">
                    {url.errorMessage}
                  </FormControl.Validation>
                )}
              </div>
            ))}
          </Stack>
        </form>
      </ScopedCommands>
      <Button leadingVisual={PlusIcon} className="tmp-mt-3" onClick={AddUrlTextInputRow}>
        Add another
      </Button>
    </Dialog>
  )
}

function UrlTextInputRow({
  index,
  removeUrlTextInputRow,
  inputCount,
  updateUrls,
  setIsUpdating,
  urlValue,
}: {
  index: number
  removeUrlTextInputRow: (index: number) => void
  inputCount: number
  updateUrls: (index: number, value: string) => void
  setIsUpdating: (isUpdating: boolean) => void
  urlValue: string
}) {
  const [url, setUrl] = useState<string>(urlValue)
  const [isDebouncing, setIsDebouncing] = useState(false)

  // Update the state when urlValue changes from removing a row
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrl(urlValue)
  }, [urlValue])

  const onChangeUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUrl(value)
    if (!isDebouncing) {
      setIsDebouncing(true)
      setIsUpdating(true)
    }
    debouncedSetUrl(value)
  }

  const debouncedSetUrl = useDebounce((value: string) => {
    updateUrls(index, value)
    setIsDebouncing(false)
    setIsUpdating(false)
  }, 400)

  return (
    <FormControl className="d-flex flex-row flex-justify-between flex-items-center width-full">
      <FormControl.Label visuallyHidden>URL</FormControl.Label>
      <TextInput
        placeholder="https://github.com/mona/playground/issues/123"
        value={url}
        onChange={onChangeUrl}
        className="width-full"
      />
      {inputCount > 1 && (
        <IconButton
          icon={TrashIcon}
          variant="invisible"
          aria-label="Remove url"
          className="ml-2"
          onClick={() => removeUrlTextInputRow(index)}
        />
      )}
    </FormControl>
  )
}
